import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import { unwrapResponse } from "@/shared/utils/response";
import type { Room } from "../types/objects.types";

type BackendRoom = {
	id: string;
	sectionId: string;
	roomNumber: string;
	floor: number;
	roomsCount: number;
	area: string;
	price: number;
	pricePerArea?: number | null;
	priceRemont: number;
	roomChoice: "tamirsiz" | "tamirli";
	status: "SOTUVDA" | "SOTILGAN" | "BAND" | "BEKOR";
	image?: string | null;
	image3d?: string | null;
	terassaImage?: string | null;
	buildingId?: string;
	section?: {
		block?: {
			buildingId: string;
			id: string;
		};
	};
};

type BackendResponse<T> = {
	success: true;
	data: T;
};

type RoomsListResponse = {
	items: BackendRoom[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

export type RoomCreatePayload = {
	roomNumber: string;
	floor: number;
	roomsCount: number;
	area: string;
	terassaArea?: string;
	hasTerassa?: boolean;
	pricePerArea: number;
	priceRemont?: number;
	roomChoice?: "tamirsiz" | "tamirli";
	sectionId: string;
	image?: string;
	image3d?: string;
	terassaImage?: string;
	isKadastr?: boolean;
	isHighlighted?: boolean;
};

export type RoomsListParams = {
	buildingId?: string;
	sectionId?: string;
	status?: string;
	floor?: string;
	roomsCount?: string;
	minPrice?: string;
	maxPrice?: string;
	limit?: number;
	page?: number;
};

const mapRoom = (item: any): Room => {
	// Check if backend returned a nested structure { rooms, sections, blocks, buildings }
	const isNested = item && typeof item === "object" && "rooms" in item;

	const room = isNested ? item.rooms : item;
	const section = isNested ? item.sections : item.section;
	const block = isNested ? item.blocks : item.section?.block;
	const building = isNested ? item.buildings : item.section?.block?.building;

	// Resolve IDs, using root properties as fallback since list returns them there sometimes
	const buildingId = room.buildingId || item.buildingId || building?.id || block?.buildingId || "";
	const blockId = room.blockId || item.blockId || block?.id || section?.blockId || "";

	let status: "sotuvda" | "sotilgan" | "band" | "bekor" = "sotuvda";
	if (room.status) {
		status = room.status.toLowerCase() as any;
	}

	// Read and parse area fields
	const area = room.area ? parseFloat(room.area) : 0;
	const terraceArea = room.terassaArea ? parseFloat(room.terassaArea) : 0;
	const pricePerArea = typeof room.pricePerArea === "number" ? room.pricePerArea : undefined;
	const images = [room.image, room.image3d, room.terassaImage].filter((image): image is string =>
		Boolean(image)
	);

	return {
		id: room.id,
		sectionId: room.sectionId,
		buildingId,
		blockId,
		roomNumber: room.roomNumber,
		floor: room.floor,
		roomsCount: room.roomsCount,
		area,
		terraceArea,
		finishType: room.roomChoice,
		basePrice: room.price || 0,
		...(pricePerArea === undefined ? {} : { pricePerArea }),
		finishedPrice: (room.price || 0) + (room.priceRemont || 0),
		hasCadastre: room.isKadastr ?? false,
		isHighlighted: room.isHighlighted ?? false,
		status,
		...(room.image ? { image: room.image } : {}),
		...(room.image3d ? { image3d: room.image3d } : {}),
		...(room.terassaImage ? { terassaImage: room.terassaImage } : {}),
		...(images.length > 0 ? { images } : {}),
		client: item.clientData
			? {
					id: item.clientData.id,
					saleId: item.clientData.saleId || undefined,
					name: item.clientData.clientName,
					avatar: item.clientData.clientName ? item.clientData.clientName.charAt(0) : "M",
					phone: item.clientData.phone,
					manager: "", // Handled on frontend details if needed
					percentPaid: item.clientData.paidPercentage || 0,
					nextPaymentDate: item.clientData.nextPaymentDate || "",
					nextPaymentAmount: item.clientData.nextPaymentAmount
						? `${item.clientData.nextPaymentAmount.toLocaleString()} UZS`
						: "",
					debtBalance: "", // Optional
					status: "Faol",
				}
			: undefined,
		installmentPrice: typeof room.installmentPrice === "number" ? room.installmentPrice : undefined,
		installmentMonths: room.installmentMonths || undefined,
		minInitialPaymentPercent:
			typeof room.minInitialPaymentPercent === "number" ? room.minInitialPaymentPercent : undefined,
	};
};

export const roomsApi = {
	getRooms: async (params: RoomsListParams = {}): Promise<Room[]> => {
		const response = await apiClient
			.get<BackendResponse<RoomsListResponse> | RoomsListResponse>(endpoints.objects.rooms, {
				params,
			})
			.catch((error) => {
				console.error("[RoomsApi] Failed to fetch rooms:", error);
				throw error;
			});
		const listResponse = unwrapResponse<RoomsListResponse>(response.data);
		return listResponse.items.map(mapRoom);
	},

	getRoomById: async (id: string): Promise<Room> => {
		const response = await apiClient
			.get<BackendResponse<BackendRoom> | BackendRoom>(endpoints.objects.roomDetail(id))
			.catch((error) => {
				console.error("[RoomsApi] Failed to fetch room:", error);
				throw error;
			});
		const room = unwrapResponse<BackendRoom>(response.data);
		return mapRoom(room);
	},

	createRoom: async (payload: RoomCreatePayload): Promise<Room> => {
		const response = await apiClient
			.post<BackendResponse<BackendRoom> | BackendRoom>(endpoints.objects.rooms, payload)
			.catch((error) => {
				console.error("[RoomsApi] Failed to create room:", error);
				throw error;
			});
		const room = unwrapResponse<BackendRoom>(response.data);
		return mapRoom(room);
	},

	updateRoom: async (id: string, payload: Partial<RoomCreatePayload>): Promise<Room> => {
		const response = await apiClient
			.patch<BackendResponse<BackendRoom> | BackendRoom>(endpoints.objects.roomDetail(id), payload)
			.catch((error) => {
				console.error("[RoomsApi] Failed to update room:", error);
				throw error;
			});
		const room = unwrapResponse<BackendRoom>(response.data);
		return mapRoom(room);
	},

	deleteRoom: async (id: string): Promise<void> => {
		await apiClient.delete(endpoints.objects.roomDetail(id)).catch((error) => {
			console.error("[RoomsApi] Failed to delete room:", error);
			throw error;
		});
	},

	setRoomPricing: async (
		id: string,
		payload: {
			installmentPrice: number;
			installmentMonths: number;
			minInitialPaymentPercent: number;
		}
	): Promise<Room> => {
		const response = await apiClient
			.patch<BackendResponse<BackendRoom> | BackendRoom>(endpoints.objects.roomPricing(id), {
				installmentPrice: Math.round(payload.installmentPrice),
				installmentMonths: payload.installmentMonths,
				minInitialPaymentPercent: payload.minInitialPaymentPercent,
			})
			.catch((error) => {
				console.error("[RoomsApi] Failed to set room pricing:", error);
				throw error;
			});
		const room = unwrapResponse<BackendRoom>(response.data);
		return mapRoom(room);
	},

	bulkUpdatePricing: async (
		items: Array<{ id: string; pricePerArea: number; installmentPrice: number }>,
		payload: {
			installmentMonths: number;
			minInitialPaymentPercent: number;
		}
	): Promise<Room[]> => {
		const response = await apiClient
			.patch<
				| BackendResponse<{ data: BackendRoom[]; updatedCount?: number }>
				| { data: BackendRoom[]; updatedCount?: number }
			>(endpoints.objects.roomsBulk, {
				items: items.map((item) => ({
					id: item.id,
					pricePerArea: Math.round(item.pricePerArea),
					installmentPrice: Math.round(item.installmentPrice),
					installmentMonths: payload.installmentMonths,
					minInitialPaymentPercent: payload.minInitialPaymentPercent,
				})),
			})
			.catch((error) => {
				console.error("[RoomsApi] Failed to bulk update pricing:", error);
				throw error;
			});
		const result = unwrapResponse<{ data: BackendRoom[]; updatedCount?: number }>(response.data);
		return result.data.map(mapRoom);
	},

	calculateSchedule: async (
		id: string,
		payload: { installmentPrice: number; monthsCount: number; initialPaymentPercent: number }
	): Promise<{
		installmentPrice: number;
		initialPaymentAmount: number;
		initialPaymentPercent: number;
		monthsCount: number;
		monthlyPayment: number;
		lastPayment: number;
		totalPaid: number;
		schedulePreview: {
			month: number;
			amount: number;
			dueDate: string;
		}[];
	}> => {
		const response = await apiClient
			.post<BackendResponse<any> | any>(endpoints.objects.calculateSchedule(id), {
				installmentPrice: Math.round(payload.installmentPrice),
				monthsCount: payload.monthsCount,
				initialPaymentPercent: payload.initialPaymentPercent,
			})
			.catch((error) => {
				console.error("[RoomsApi] Failed to calculate schedule:", error);
				throw error;
			});
		const data = unwrapResponse<any>(response.data);
		return {
			installmentPrice: data.installmentPrice,
			initialPaymentAmount: data.initialPaymentAmount,
			initialPaymentPercent: data.initialPaymentPercent,
			monthsCount: data.monthsCount,
			monthlyPayment: data.monthlyPayment,
			lastPayment: data.lastPayment,
			totalPaid: data.totalPaid,
			schedulePreview: (data.schedulePreview || []).map((item: any) => ({
				month: item.month,
				amount: item.amount,
				dueDate: item.dueDate,
			})),
		};
	},
};
